import { FunctionComponent, MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";

import { Email } from "@/pages/Private/pages/Account/schema/email";

import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";
import { SidebarEmailReconnectMenu } from "./SidebarEmailReconnectMenu";

interface SidebarEmailWarningProps {
	/** The mailboxes whose provider session is logged out (`isEmailLoggedOut`). */
	emails: Email[];
	/** Render the compact icon-only variant used by the collapsed sidebar. */
	collapsed?: boolean;
	/** Starts the OAuth reconnect for one mailbox (opens the provider login in a new tab). */
	onReconnect: (emailId: number) => void;
}

/**
 * One warning chip for all logged-out mailboxes, styled like the LinkedIn chip beside it. A full
 * address plus "is logged out. Click to reconnect" never fits the 248px sidebar (the old
 * per-mailbox pills were `whitespace-nowrap` and clipped at the edge), so the chip only counts and
 * the tooltip lists the addresses. Clicking reconnects directly when one mailbox is affected and
 * opens a picker when several are.
 */
export const SidebarEmailWarning: FunctionComponent<SidebarEmailWarningProps> = ({
	emails,
	collapsed = false,
	onReconnect,
}) => {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const closeMenu = useCallback(() => setMenuAnchor(null), []);

	if (emails.length === 0) {
		return null;
	}

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		if (emails.length === 1) {
			onReconnect(emails[0].id);

			return;
		}

		const anchor = event.currentTarget;

		setMenuAnchor((current) => (current ? null : anchor));
	};

	const handleSelect = (emailId: number) => {
		setMenuAnchor(null);
		onReconnect(emailId);
	};

	const tooltip = (
		<div className="flex flex-col gap-0.5">
			{emails.map((email) => (
				<div key={email.id}>{email.email}</div>
			))}
			<div className="text-text-meta">{t("sidebar.clickToReconnect")}</div>
		</div>
	);

	const menu = menuAnchor && (
		<SidebarEmailReconnectMenu
			anchorEl={menuAnchor}
			emails={emails}
			onClose={closeMenu}
			onSelect={handleSelect}
		/>
	);

	if (collapsed) {
		return (
			<>
				<div className="cursor-pointer" onClick={handleClick}>
					<Tooltip content={tooltip} disabled={!!menuAnchor}>
						<div className="w-[24px] h-[24px] rounded-full border border-text-warning font-bold bg-background-warning flex items-center justify-center">
							<Icon className="w-[12px] h-[12px] text-text-warning" icon={faWarning} />
						</div>
					</Tooltip>
				</div>
				{menu}
			</>
		);
	}

	return (
		<>
			<Tooltip content={tooltip} direction="bottom" disabled={!!menuAnchor}>
				<div
					className="relative text-sm items-center cursor-pointer rounded-xl border border-text-warning bg-background-warning p-2 z-10 whitespace-normal break-words min-w-fit"
					onClick={handleClick}
				>
					<span className="text-text-warning flex justify-center w-full h-full items-center gap-2">
						<Icon className="w-[16px] h-[16px] text-text-warning" icon={faWarning} />
						<span>{t("sidebar.emailAccountsLoggedOut", { count: emails.length })}</span>
					</span>
				</div>
			</Tooltip>
			{menu}
		</>
	);
};
